import { Box, Link, useTheme } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { BackContinue } from '@components/back_continue/back_continue'
import { i18WithComponent as t } from '@shared/locale'
import { detailsPoliciesSchema, getProfileValues, setupInitialValues } from './details_policies_schema'
// import type { TDetailsPoliciesSchema } from './details_policies.d'
import { ContactInformation } from './contact_information'
// import { VaccinationRecords } from './vaccination_records'
import { useBookingContext } from '@providers/booking'
import { FlexCardDisplay, FlexCardField, Spacer, TCardEntry } from 'components-ui'
import { Services, Types } from 'service-api'
// import { SpecialInstructions } from './special_instructions'
import { useLocationContext } from '@providers/location'
import { STICKY_FOOTER_HEIGHT } from '@shared/constants'
import { buildErrorMessage } from '@shared/error'
import { backNavigationFromDetailsPolicies, continueNavigation } from '@shared/header_navigation'
import {
  bookingConfigsFromReservation,
  mergeBookingConfigPreferSearchHours,
} from '@shared/surcharge_policy_arrival_adapters'
import { useRoutes } from 'app/use_routes'
import { isVaccinationEnabled, isVaccinationRequired, isVetEnabled, shallowEqual } from 'components-ui/src/shared'
import { SMSConsentError } from 'components-ui/src/shared/error'
import { captureException, captureError } from 'service-api/src/shared'
import { SurchargePolicy } from 'components-ui/src/purchase_summary/surcharge_policy/surcharge_policy'
import { buildOrderInput, inputCardGroupsBuilder } from './helper'
import { VaccinationRecords } from './vaccination_records'
import { VeterinarianInformation } from './veterinarian_information'
import { useSessionContext } from '@providers/session'
import { GenericSimpleBag } from 'service-api/src/types'

const ENABLE_VAX_VET = false

export const DetailsPoliciesForm = ({ reservation }: { reservation: Types.BookingReservationClientDto }) => {
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const { location } = useLocationContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const renderStyle = useMediaQuery(theme.breakpoints.down('md')) ? 'mobile' : 'desktop'
  const isMobile = renderStyle === 'mobile'
  const { navigateRoute } = useRoutes()
  const serviceTypeName = reservation.order?.primaryServiceType?.serviceType?.name ?? ''
  const lupId = sessionCtx.state.user?.lupId
  const [consentToSMS, setConsentToSMS] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})

  const inputCardGroups = useMemo(
    () => (serviceTypeName === 'packages' ? [] : inputCardGroupsBuilder(reservation.flexBookingConfs || [])),
    [reservation.flexBookingConfs, serviceTypeName]
  )

  const formik = useFormik({
    initialValues: setupInitialValues(bookingCtx.state.cart),
    enableReinitialize: true,
    validationSchema: detailsPoliciesSchema(reservation, inputCardGroups),
    onSubmit: async (values: GenericSimpleBag): Promise<boolean> => {
      const data = buildOrderInput(values, reservation, inputCardGroups, bookingCtx.state.cart)
      const initialProfile = getProfileValues(formik.initialValues)
      const updatedProfile: GenericSimpleBag = getProfileValues(values)

      const isProfileUpdated = !shallowEqual(initialProfile, updatedProfile)

      try {
        if (consentToSMS) {
          Services.ClientSMSConsentService.updateSMSConsent(locationName, {
            consentType: 'SMS',
            lupId: lupId || '',
            phoneNumber: values.phone || '',
            operatorEmail: sessionCtx.state.user?.email || '',
            consentStatus: 'ACTIVE',
          })
            .then((res) => {
              // There is a case where we get a 200 but success is set to false. This means that we are going to check Reach for a status change,
              // so we don't want to let them know there was a successful change when there wasn't but we also don't want to block the next action either.
              // So we do nothing in that "or" case below
              if (res?.success) {
                enqueueSnackbar(t('contactForm.smsConsent.success'), { variant: 'success' })
              }
              bookingCtx.dispatch({
                type: 'SET_CONTACT_FIELD',
                payload: { value: { fieldName: 'smsConsentStatus', value: 'ACTIVE' } },
              })
            })
            .catch((e) => {
              captureException(new SMSConsentError((e as Error).message), {
                context: {
                  locationUserProfileId: lupId || '',
                },
              })
              enqueueSnackbar(buildErrorMessage(e), { variant: 'error' })
            })
        }
        if (isProfileUpdated) {
          await Services.ClientLocationUserProfileService.put(locationName, lupId || '', updatedProfile)
        }

        const order = await Services.ClientBookingReservationService.updateReservation(
          reservation.order!.id!,
          locationName,
          data
        )
        bookingCtx.dispatch({
          type: 'SET_PAYMENT_CONTACT',
          payload: { value: values },
        })
        bookingCtx.dispatch({
          type: 'SET_RESERVATION_DATA',
          payload: { value: order },
        })
        return true
      } catch (err) {
        enqueueSnackbar(buildErrorMessage(err), { variant: 'error' })
        captureError(err as Error)
        return false
      }
    },
  })

  useEffect(() => {
    if (formik.submitCount > submitCount && Object.keys(formik.errors).length > 0) {
      const firstErrorField = Object.keys(formik.errors)[0]
      const errorElement = fieldRefs.current[firstErrorField]

      if (errorElement instanceof HTMLElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        errorElement.focus?.()
      }
    }
    setSubmitCount(formik.submitCount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount, formik.errors])

  const onBackHandler = useCallback(
    async (ev?: React.SyntheticEvent) => {
      ev?.preventDefault()
      if (serviceTypeName === 'temper-test') {
        navigateRoute('temperTest', { serviceTypeName })
      } else {
        const route = backNavigationFromDetailsPolicies(bookingCtx)
        navigateRoute(route, { serviceTypeName })
      }
    },
    [bookingCtx, navigateRoute, serviceTypeName]
  )

  const onContinueHandler = useCallback(
    async (ev?: React.SyntheticEvent) => {
      ev?.preventDefault()
      if (await formik.submitForm()) {
        const route = await continueNavigation(bookingCtx, locationName)
        if (route) {
          navigateRoute(route, { serviceTypeName })
        }
      }

      // always returns false because we are handling the navigation too
      // this way short-circuit the BackContinue navigation handling
      return false
    },
    [locationName, bookingCtx, formik, navigateRoute, serviceTypeName]
  )

  const reservationBookingConfig = useMemo(() => bookingConfigsFromReservation(reservation), [reservation])

  const surchargeBookingConfig = useMemo(
    () => mergeBookingConfigPreferSearchHours(reservationBookingConfig, bookingCtx.state.config.booking),
    [reservationBookingConfig, bookingCtx.state.config.booking]
  )

  const policies = useMemo(() => {
    const policyByGroup = (reservation.flexBookingConfs || []).reduce<Record<string, TCardEntry[]>>((res, c) => {
      if (
        c.group &&
        c.target === 'ORDER' &&
        (c.step === 'BOOKING' || c.step === 'ALL') &&
        (c.type === 'INFO' || c.type === 'URL')
      ) {
        const groupedList = res?.[c.group] ?? []
        return {
          ...res,
          [c.group]: [...groupedList, c],
        }
      }
      return res
    }, {})
    return Object.values(policyByGroup)
  }, [reservation])

  // NOTE: the formId is used with the header's Continue button, to be able to submit
  // a form from outside the component tree
  return (
    <form id="details-policies-form" action="" onSubmit={onContinueHandler} style={{ width: '100%' }}>
      <Stack direction={{ sx: 'column', md: 'row' }} alignItems="start" spacing={2} flex={1} mb={2}>
        <Stack direction="column" spacing={3} flex={1} width="100%">
          <ContactInformation
            formik={formik}
            consentToSMS={consentToSMS}
            handleConsentToggle={() => setConsentToSMS((prev) => !prev)}
            fieldRefs={fieldRefs}
          />
          {ENABLE_VAX_VET && isVetEnabled(reservation.locationBookingConf!) && (
            <VeterinarianInformation formik={formik} fieldRefs={fieldRefs} />
          )}
          {ENABLE_VAX_VET && isVaccinationEnabled(reservation.locationBookingConf!) && (
            <VaccinationRecords
              required={isVaccinationRequired(reservation.locationBookingConf!)}
              reservation={reservation}
            />
          )}
          {/* <SpecialInstructions formik={formik} /> */}
          {inputCardGroups.map((group, idx) => {
            return formik && <FlexCardField key={idx} fields={group} formik={formik} fieldRefs={fieldRefs} />
          })}
        </Stack>
        <Stack direction="column" spacing={3} flex={1} mt={isMobile ? 3 : 0} width="100%">
          {policies.map((policy, idx) => (
            <FlexCardDisplay key={idx} cardData={policy} />
          ))}
          <Box>
            {reservation.order && surchargeBookingConfig && (
              <SurchargePolicy order={reservation.order} bookingConfig={surchargeBookingConfig} />
            )}
          </Box>
          <Box sx={{ padding: isMobile ? 2 : 0 }}>
            <Typography variant="body1" sx={{ opacity: 0.8 }} textAlign="left">
              {t('detailsPolicies.messageConsent', {
                facilityName: location?.displayName || '',
                terms: (
                  <Link target="_blank" href={`/${locationName}/privacy-policy`} key="terms">
                    {t('detailsPolicies.terms')}
                  </Link>
                ),
              })}
            </Typography>
            <Spacer height={2} />
            <Typography variant="body1" sx={{ opacity: 0.8 }} textAlign="left">
              {t('detailsPolicies.confirmation', {
                termConditionsLink: (
                  <Link target="_blank" href={'https://www.goose.pet/terms-conditions'} key="terms">
                    {t('detailsPolicies.tcAnchorText')}
                  </Link>
                ),
              })}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Spacer height={STICKY_FOOTER_HEIGHT} />
      <BackContinue
        continueLabel={t('detailsPolicies.continueButton') as string}
        onContinue={onContinueHandler}
        onBack={onBackHandler}
      />
    </form>
  )
}
